import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { cn } from '../lib/cn'
import { focusRingInset } from '../lib/focus'
import { useFieldControlProps } from './Field'

const CONTROL_BASE =
  'w-full rounded-md border bg-surface-inset px-3 text-body-sm text-text-primary placeholder:text-text-tertiary ' +
  'border-border-control transition-colors duration-fast ease-standard ' +
  'hover:border-border-strong aria-[invalid=true]:border-state-critical disabled:opacity-50 disabled:cursor-not-allowed'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  iconStart?: ReactNode
  iconEnd?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, iconStart, iconEnd, ...rest },
  ref,
) {
  const fieldProps = useFieldControlProps()
  const control = (
    <input
      ref={ref}
      className={cn(
        CONTROL_BASE,
        'h-10',
        iconStart && 'ps-9',
        iconEnd && 'pe-9',
        focusRingInset,
        className,
      )}
      {...fieldProps}
      {...rest}
    />
  )

  if (!iconStart && !iconEnd) return control
  return (
    <div className="relative">
      {iconStart && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-text-tertiary"
        >
          {iconStart}
        </span>
      )}
      {control}
      {iconEnd && (
        <span className="absolute inset-y-0 end-3 flex items-center text-text-tertiary">
          {iconEnd}
        </span>
      )}
    </div>
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    const fieldProps = useFieldControlProps()
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(CONTROL_BASE, 'py-2 leading-relaxed', focusRingInset, className)}
        {...fieldProps}
        {...rest}
      />
    )
  },
)

/**
 * Character counter for the free-text fields the plan caps at 500 chars
 * (DEC-08). Announced politely so a screen-reader user is not spammed on every
 * keystroke, but still learns the limit before submitting.
 */
export function CharacterCount({
  value,
  max,
  className,
}: {
  value: string
  max: number
  className?: string
}) {
  const remaining = max - value.length
  const over = remaining < 0
  return (
    <p
      aria-live="polite"
      className={cn(
        'text-caption tabular-nums',
        over ? 'text-state-critical' : 'text-text-secondary',
        className,
      )}
    >
      {over ? `${Math.abs(remaining)} characters over the ${max} limit` : `${remaining} characters left`}
    </p>
  )
}
